import { useRef, useState, useEffect } from 'react';
import useDebounce from '@hooks/useDebounce';

type ListenerOptions = {
  passive?: boolean;
  capture?: boolean;
};

export default function useRect<T extends HTMLElement>(
  events: Array<'resize' | 'scroll'> = ['resize', 'scroll'],
  options: ListenerOptions | null = { passive: true, capture: false },
  debounce = 0,
) {
  const ref = useRef<T>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const { passive, capture } = {
    passive: true,
    capture: false,
    ...(options || {}),
  };

  // stabalize events by using string
  const eventsString = events.sort().join(' ');
  const handler = useDebounce(() => {
    if (!ref.current) return;
    setRect(ref.current.getBoundingClientRect());
  }, debounce);

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    if (
      typeof ResizeObserver !== 'undefined' &&
      ref.current &&
      eventsString.indexOf('resize') >= 0
    ) {
      resizeObserver = new ResizeObserver(handler);
      resizeObserver.observe(ref.current);
    }

    if (eventsString.indexOf('resize') >= 0) window.addEventListener('resize', handler, { capture, passive });
    if (eventsString.indexOf('scroll') >= 0) window.addEventListener('scroll', handler, { capture, passive });
    handler();
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handler, { capture });
      window.removeEventListener('scroll', handler, { capture });
    };
  }, [handler, eventsString, capture, passive]);

  return [ref, rect] as const;
}
